import React from 'react';
import { Box, Grid, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Stack, Button, IconButton, Select, MenuItem, Tooltip, Checkbox, ListItemText, FormControlLabel, InputAdornment, Radio, RadioGroup, FormControl, FormLabel } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';

const SECTION_ROWS = [
  'General', 'Lid/Margin', 'Conjunctiva', 'Limbus', 'Cornea', 'AC/Aqueous', 'Iris', 'Angle'
];

const SECTION_COLORS = {
  'General': '#1976d2',
  'Lid/Margin': '#8e24aa',
  'Conjunctiva': '#2e7d32',
  'Limbus': '#ef6c00',
  'Cornea': '#d32f2f',
  'AC/Aqueous': '#00796b',
  'Iris': '#5d4037',
  'Angle': '#455a64',
};
const UNASSIGNED_KEY = '__unassigned__';

const MARKER_OPTIONS = [
  { id: 'dot', label: 'Dot' },
  { id: 'x', label: 'X' },
  { id: 'triangle', label: 'Triangle' },
];

export function getDefaultExternValues() {
  const makeRows = () => SECTION_ROWS.reduce((acc, r) => ({ ...acc, [r]: { od: '', os: '' } }), {});
  return {
    findings: makeRows(),
    diagrams: {
      od: [], // [{x,y}]
      os: [],
    },
    tonometry: {
      time: '',
      warned: false,
      type: '', // Goldmann | Perkins | Palpation | NCT
      od: '', // value in mmHg
      os: '', // value in mmHg
      drops: '', // selected label string
    },
    pupilSize: {
      od: { light: '', dark: '' },
      os: { light: '', dark: '' },
    },
    pupilReactions: {
      perrla: false,
      rapd: '', // 'neg' | 'pos'
    },
  };
}

function TwoEyeCanvas({ value, onChange }) {
  const canvasRefOD = React.useRef(null);
  const canvasRefOS = React.useRef(null);
  const [editing, setEditing] = React.useState(true);
  const [marker, setMarker] = React.useState('dot');
  const [area, setArea] = React.useState('');
  const [filterSections, setFilterSections] = React.useState([]); // [] = all
  const [tipOD, setTipOD] = React.useState({ show: false, x: 0, y: 0, text: '' });
  const [tipOS, setTipOS] = React.useState({ show: false, x: 0, y: 0, text: '' });

  const drawMarker = (ctx, px, py, type) => {
    const r = 4;
    switch (type) {
      case 'x': {
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(px - r, py - r);
        ctx.lineTo(px + r, py + r);
        ctx.moveTo(px + r, py - r);
        ctx.lineTo(px - r, py + r);
        ctx.stroke();
        break;
      }
      case 'triangle': {
        ctx.beginPath();
        ctx.moveTo(px, py - r);
        ctx.lineTo(px - r, py + r);
        ctx.lineTo(px + r, py + r);
        ctx.closePath();
        ctx.fill();
        break;
      }
      case 'dot':
      default: {
        ctx.beginPath();
        ctx.arc(px, py, r, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
    }
  };

  const drawEye = (ctx, w, h) => {
    // Stylized almond-shaped eye with iris and pupil
    ctx.clearRect(0, 0, w, h);

    const cx = w / 2;
    const cy = h / 2;
  const margin = 2; // reduced to allow a slightly larger outline
  const rx = Math.max((w / 2) - margin, 10); // horizontal radius for eyelids
  const ry = Math.max(Math.min((h / 2) - margin, rx * 0.7), 10); // vertical radius a bit larger to better contain pupil

    // Eyelid outline (almond)
    ctx.beginPath();
    ctx.moveTo(cx - rx, cy);
    ctx.quadraticCurveTo(cx, cy - ry, cx + rx, cy); // upper lid
    ctx.quadraticCurveTo(cx, cy + ry, cx - rx, cy); // lower lid
    ctx.closePath();
    ctx.strokeStyle = '#555';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Iris
    const irisR = Math.min(rx, ry) * 0.50;
    ctx.beginPath();
    ctx.arc(cx, cy, irisR, 0, Math.PI * 2);
    ctx.strokeStyle = '#999';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Pupil
    const pupilR = irisR * 0.35;
    ctx.beginPath();
    ctx.arc(cx, cy, pupilR, 0, Math.PI * 2);
    ctx.fillStyle = '#333';
    ctx.fill();

    // Small highlight on pupil for depth
    ctx.beginPath();
    ctx.arc(cx - pupilR * 0.4, cy - pupilR * 0.4, pupilR * 0.15, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.fill();
  };

  const drawAll = React.useCallback(() => {
    const canvases = [
      { ref: canvasRefOD, points: value?.od || [] },
      { ref: canvasRefOS, points: value?.os || [] },
    ];
    canvases.forEach(({ ref, points }) => {
      const c = ref.current;
      if (!c) return;
      const dpr = window.devicePixelRatio || 1;
      const rect = c.getBoundingClientRect();
      c.width = rect.width * dpr;
      c.height = rect.height * dpr;
      const ctx = c.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      drawEye(ctx, rect.width, rect.height);
      // draw points using shape type and section color; apply filter
      points.forEach(p => {
        const sec = p.s || UNASSIGNED_KEY;
        if (filterSections.length > 0 && !filterSections.includes(sec)) return;
        const px = p.x * rect.width;
        const py = p.y * rect.height;
        const color = SECTION_COLORS[p.s] || '#9e9e9e';
        ctx.strokeStyle = color; ctx.fillStyle = color;
        drawMarker(ctx, px, py, p.t || 'dot');
      });
    });
  }, [value?.od, value?.os, filterSections]);

  React.useEffect(() => { drawAll(); }, [drawAll]);

  const handleClick = (side, e) => {
    if (!editing) return;
    const c = side === 'od' ? canvasRefOD.current : canvasRefOS.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const next = { od: [...(value?.od || [])], os: [...(value?.os || [])] };
    next[side].push({ x, y, t: marker, s: area || null });
    onChange?.(next);
  };

  const handleMove = (side, e) => {
    const c = side === 'od' ? canvasRefOD.current : canvasRefOS.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const mx = (e.clientX - rect.left) / rect.width;
    const my = (e.clientY - rect.top) / rect.height;
    const list = (side === 'od' ? value?.od : value?.os) || [];
    const thresholdPx = 8;
    let found = null;
    for (let i = 0; i < list.length; i++) {
      const p = list[i];
      const px = p.x; const py = p.y;
      const dx = (mx - px) * rect.width; const dy = (my - py) * rect.height;
      if (Math.sqrt(dx*dx + dy*dy) <= thresholdPx) { found = p; break; }
    }
    const setTip = side === 'od' ? setTipOD : setTipOS;
    if (found) {
      const secLabel = found.s || 'Unassigned';
      const msg = `${side.toUpperCase()} • ${secLabel} • ${found.t || 'dot'}`;
      setTip({ show: true, x: e.clientX - rect.left, y: e.clientY - rect.top, text: msg });
    } else {
      setTip(prev => prev.show ? { ...prev, show: false } : prev);
    }
  };
  const handleLeave = (side) => {
    (side === 'od' ? setTipOD : setTipOS)(prev => prev.show ? { ...prev, show: false } : prev);
  };

  const undo = (side) => {
    const list = [...(value?.[side] || [])];
    list.pop();
    onChange?.({ ...value, [side]: list });
  };

  const clear = (side) => {
    onChange?.({ ...value, [side]: [] });
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
              <Tooltip title={editing ? 'Disable Edit' : 'Enable Edit'}>
                <IconButton size="small" onClick={() => setEditing(e => !e)} color={editing ? 'primary' : 'default'}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Save OD as PNG">
                <IconButton size="small" onClick={() => {
                  const c = canvasRefOD.current; if (!c) return; const link = document.createElement('a'); link.download = 'diagram-od.png'; link.href = c.toDataURL('image/png'); link.click();
                }}>
                  <SaveAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Save OS as PNG">
                <IconButton size="small" onClick={() => {
                  const c = canvasRefOS.current; if (!c) return; const link = document.createElement('a'); link.download = 'diagram-os.png'; link.href = c.toDataURL('image/png'); link.click();
                }}>
                  <SaveAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clear both diagrams">
                <IconButton size="small" onClick={() => onChange?.({ od: [], os: [] })}>
                  <DeleteSweepIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, justifyContent: 'flex-end', ml: 2, flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption">Marker</Typography>
                <Select size="small" value={marker} onChange={(e) => setMarker(e.target.value)} sx={{ minWidth: 120 }}>
                  {MARKER_OPTIONS.map(m => (
                    <MenuItem key={m.id} value={m.id}>{m.label}</MenuItem>
                  ))}
                </Select>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption">Area</Typography>
                <Select size="small" value={area} onChange={(e) => setArea(e.target.value)} displayEmpty sx={{ minWidth: 180 }}>
                  <MenuItem value=""><em>Associate with section…</em></MenuItem>
                  {SECTION_ROWS.map(r => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </Select>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption">Filter</Typography>
                <Select
                  multiple size="small"
                  value={filterSections}
                  onChange={(e) => setFilterSections(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected || selected.length === 0) return <em>All sections</em>;
                    if (selected.length === 1) {
                      const v = selected[0];
                      return v === UNASSIGNED_KEY ? 'Unassigned' : v;
                    }
                    return `${selected.length} selected`;
                  }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                  sx={{
                    minWidth: 180,
                    maxWidth: 220,
                    '& .MuiSelect-select': {
                      display: 'block',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                  }}
                >
                  <MenuItem value={UNASSIGNED_KEY}>
                    <Checkbox checked={filterSections.indexOf(UNASSIGNED_KEY) > -1} />
                    <ListItemText primary="Unassigned" />
                  </MenuItem>
                  {SECTION_ROWS.map((r) => (
                    <MenuItem key={r} value={r}>
                      <Checkbox checked={filterSections.indexOf(r) > -1} />
                      <ListItemText primary={r} />
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
            </Box>
          </Box>
          {/* Legend */}
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
            {SECTION_ROWS.map((s) => (
              <Stack key={s} direction="row" spacing={0.5} alignItems="center">
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: SECTION_COLORS[s] }} />
                <Typography variant="caption" color="text.secondary">{s}</Typography>
              </Stack>
            ))}
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#9e9e9e' }} />
              <Typography variant="caption" color="text.secondary">Unassigned</Typography>
            </Stack>
          </Stack>
        </Paper>
      </Grid>
      {/* OD */}
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">OD</Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => undo('od')}>Undo</Button>
              <Button size="small" onClick={() => clear('od')}>Clear</Button>
            </Stack>
          </Box>
          <Box sx={{ position: 'relative', border: '1px solid #ddd', borderRadius: 1, height: 200 }}>
            <canvas
              ref={canvasRefOD}
              style={{ width: '100%', height: '100%', display: 'block', cursor: editing ? 'crosshair' : 'default' }}
              onClick={(e) => handleClick('od', e)}
              onMouseMove={(e) => handleMove('od', e)}
              onMouseLeave={() => handleLeave('od')}
            />
            {tipOD.show && (
              <Box sx={{ position: 'absolute', left: tipOD.x, top: tipOD.y, transform: 'translate(8px, 8px)', pointerEvents: 'none', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1, py: 0.5, boxShadow: 1 }}>
                <Typography variant="caption">{tipOD.text}</Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Grid>
      {/* OS */}
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">OS</Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => undo('os')}>Undo</Button>
              <Button size="small" onClick={() => clear('os')}>Clear</Button>
            </Stack>
          </Box>
          <Box sx={{ position: 'relative', border: '1px solid #ddd', borderRadius: 1, height: 200 }}>
            <canvas
              ref={canvasRefOS}
              style={{ width: '100%', height: '100%', display: 'block', cursor: editing ? 'crosshair' : 'default' }}
              onClick={(e) => handleClick('os', e)}
              onMouseMove={(e) => handleMove('os', e)}
              onMouseLeave={() => handleLeave('os')}
            />
            {tipOS.show && (
              <Box sx={{ position: 'absolute', left: tipOS.x, top: tipOS.y, transform: 'translate(8px, 8px)', pointerEvents: 'none', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1, py: 0.5, boxShadow: 1 }}>
                <Typography variant="caption">{tipOS.text}</Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
}

export default function ExternTab({ value, onChange }) {
  const v = value || getDefaultExternValues();
  const defaultTono = React.useMemo(() => ({ time: '', warned: false, type: '', od: '', os: '', drops: '' }), []);
  const t = v.tonometry || defaultTono;
  const defaultPupilSize = React.useMemo(() => ({ od: { light: '', dark: '' }, os: { light: '', dark: '' } }), []);
  const ps = v.pupilSize || defaultPupilSize;
  const defaultPupilReactions = React.useMemo(() => ({ perrla: false, rapd: '' }), []);
  const pr = v.pupilReactions || defaultPupilReactions;

  const setFinding = (row, side, text) => {
    const findings = { ...v.findings, [row]: { ...v.findings[row], [side]: text } };
    onChange?.({ ...v, findings });
  };

  const setTonometry = (key, val) => {
    const tonometry = { ...t, [key]: val };
    onChange?.({ ...v, tonometry });
  };

  const setNowTime = () => {
    const d = new Date();
    let hours = d.getHours();
    const minutes = d.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12; if (hours === 0) hours = 12;
    const mm = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const timeStr = `${hours}:${mm} ${ampm}`;
    setTonometry('time', timeStr);
  };

  const setPupilSize = (eye, cond, val) => {
    const pupilSize = {
      ...ps,
      [eye]: { ...ps[eye], [cond]: val },
    };
    onChange?.({ ...v, pupilSize });
  };

  const setPupilReactions = (key, val) => {
    const pupilReactions = { ...pr, [key]: val };
    onChange?.({ ...v, pupilReactions });
  };

  return (
    <Box>
      {/* Row 1 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Anterior Segment</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }} />
                    <TableCell sx={{ fontWeight: 600 }}>OD</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>OS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {SECTION_ROWS.map((r) => (
                    <TableRow key={r}>
                      <TableCell sx={{ width: 160, fontWeight: 600 }}>{r}</TableCell>
                      <TableCell>
                        <TextField size="small" fullWidth value={v.findings[r].od} onChange={(e) => setFinding(r, 'od', e.target.value)} />
                      </TableCell>
                      <TableCell>
                        <TextField size="small" fullWidth value={v.findings[r].os} onChange={(e) => setFinding(r, 'os', e.target.value)} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Diagrams</Typography>
            <TwoEyeCanvas value={v.diagrams} onChange={(diagrams) => onChange?.({ ...v, diagrams })} />
          </Paper>
        </Grid>
      </Grid>

      {/* TODO: Row 2 - 4 sections will be added as per later specs */}
      {/* Row 2: Tonometry */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Tonometry</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    label="Time"
                    value={t.time}
                    onChange={(e) => setTonometry('time', e.target.value)}
                    sx={{ flex: 1, minWidth: 160 }}
                  />
                  <Button size="small" variant="outlined" onClick={setNowTime}>Now</Button>
                  <FormControlLabel
                    control={<Checkbox checked={!!t.warned} onChange={(e) => setTonometry('warned', e.target.checked)} />}
                    label="Warned"
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={3}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption">Type</Typography>
                  <Select size="small" value={t.type} onChange={(e) => setTonometry('type', e.target.value)} displayEmpty sx={{ minWidth: 160 }}>
                    <MenuItem value=""><em>Select type…</em></MenuItem>
                    {['Goldmann', 'Perkins', 'Palpation', 'NCT'].map(opt => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    label="OD"
                    value={t.od}
                    onChange={(e) => setTonometry('od', e.target.value)}
                    InputProps={{ endAdornment: <InputAdornment position="end">mmHg</InputAdornment> }}
                    sx={{ minWidth: 120 }}
                  />
                  <TextField
                    size="small"
                    label="OS"
                    value={t.os}
                    onChange={(e) => setTonometry('os', e.target.value)}
                    InputProps={{ endAdornment: <InputAdornment position="end">mmHg</InputAdornment> }}
                    sx={{ minWidth: 120 }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption">Drops</Typography>
                  <Select size="small" value={t.drops} onChange={(e) => setTonometry('drops', e.target.value)} displayEmpty sx={{ minWidth: 360, maxWidth: '100%' }}>
                    <MenuItem value=""><em>Select drops…</em></MenuItem>
                    <MenuItem value="Fluorescein Sodium (0.25%) and Benoxinate Hydrochloride (0.4%)">Fluorescein Sodium (0.25%) and Benoxinate Hydrochloride (0.4%)</MenuItem>
                    <MenuItem value="Alcaine with fluorescein Strips">Alcaine with fluorescein Strips</MenuItem>
                  </Select>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Row 3: Pupil Size */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Pupil Size</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ minWidth: 64 }} variant="body2">Light</Typography>
                  <TextField
                    size="small"
                    label="OD"
                    value={ps.od.light}
                    onChange={(e) => setPupilSize('od', 'light', e.target.value)}
                    InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }}
                    sx={{ minWidth: 120 }}
                  />
                  <TextField
                    size="small"
                    label="OS"
                    value={ps.os.light}
                    onChange={(e) => setPupilSize('os', 'light', e.target.value)}
                    InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }}
                    sx={{ minWidth: 120 }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ minWidth: 64 }} variant="body2">Dark</Typography>
                  <TextField
                    size="small"
                    label="OD"
                    value={ps.od.dark}
                    onChange={(e) => setPupilSize('od', 'dark', e.target.value)}
                    InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }}
                    sx={{ minWidth: 120 }}
                  />
                  <TextField
                    size="small"
                    label="OS"
                    value={ps.os.dark}
                    onChange={(e) => setPupilSize('os', 'dark', e.target.value)}
                    InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }}
                    sx={{ minWidth: 120 }}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Row 4: PERRLA / RAPD */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>PERRLA / RAPD</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <FormControl>
                  <FormLabel sx={{ typography: 'caption', color: 'text.secondary' }}>
                    <Tooltip title="Pupils Equal, Round, Reactive to Light and Accommodation">
                      <span>PERRLA</span>
                    </Tooltip>
                  </FormLabel>
                  <RadioGroup
                    row
                    value={pr.perrla ? 'yes' : 'no'}
                    onChange={(e) => setPupilReactions('perrla', e.target.value === 'yes')}
                  >
                    <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl>
                  <FormLabel sx={{ typography: 'caption', color: 'text.secondary' }}>
                    <Tooltip title="Relative Afferent Pupillary Defect (Marcus Gunn). Negative = absent, Positive = present.">
                      <span>RAPD</span>
                    </Tooltip>
                  </FormLabel>
                  <RadioGroup
                    row
                    value={pr.rapd}
                    onChange={(e) => setPupilReactions('rapd', e.target.value)}
                  >
                    <FormControlLabel value="neg" control={<Radio size="small" />} label="Negative" />
                    <FormControlLabel value="pos" control={<Radio size="small" />} label="Positive" />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
